import { useEffect, useState, useRef, lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  NavLink,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import "./housestyle.css";
import logo from "./Media/logo.png";
import logoS from "./Media/logo_s.png";
import commandBlock from "./Icons/Icons/commandblock.png";
import api, { SITE_BASE } from "./API/Api";

import Home from "./Home/Home";
import Apply from "./Apply/Apply";
import Blogs from "./Blogs/Blogs";
import BlogPost from "./Blogs/BlogPost";
import Rules from "./Rules/Rules";
import StaffPage from "./Staff/Staff";
import GiveawayPage from "./Giveaways/Giveaways";
import Ranks from "./Ranks/Ranks";
import SeasonInfo from "./SeasonInfo/SeasonInfo";
import Wiki from "./Wiki/Wiki";
import { PageWrapper, FadeIn } from "./components/Animations";
import { AuthProvider } from "./components/AuthContext";
import { McIcon } from "./components/CraftingGrid";

const AdminPage = lazy(() => import('./Admin/Admin'));

// Wiki is still being built out - only reachable on a local dev machine for now.
// Doesn't gate /wiki/admin (a separate route, still needed by staff in production).
function isLocalDev() {
  return typeof window !== "undefined"
    && ["localhost", "127.0.0.1"].includes(window.location.hostname);
}

function DiscountBanner() {
  const [sale, setSale] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api("getsale").then((res) => {
      if (res.isSuccess && res.data) setSale(res.data);
    });
  }, []);

  if (!sale?.active) return null;

  function copy() {
    navigator.clipboard.writeText(sale.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="discount-banner">
      {sale.label && <span className="discount-banner-text">{sale.label}</span>}
      <button className="discount-code-chip" onClick={copy}>
        {sale.code}
        <span className="discount-code-chip-copy">
          {copied ? "Copied!" : "Copy"}
        </span>
      </button>
      <NavLink to="/store" className="discount-banner-cta">
        Check it out →
      </NavLink>
    </div>
  );
}

function ComingSoon({ title }) {
  return (
    <div style={{ padding: "4rem 2rem", textAlign: "center" }}>
      <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🚧</div>
      <h2 style={{ fontSize: "var(--t-2xl)", marginBottom: "0.5rem" }}>
        {title}
      </h2>
      <p style={{ color: "var(--text-muted)", fontSize: "var(--t-sm)" }}>
        This page is coming soon.
      </p>
    </div>
  );
}

const RANK_COLORS = {
  earth: { color: "#7cba46", color2: "#4e8c2f", label: "Earth" },
  wind:  { color: "#8fd8e8", color2: "#5fb8d4", label: "Wind" },
  water: { color: "#4a9ee0", color2: "#2f6fc4", label: "Water" },
  fire:  { color: "#e8642c", color2: "#c23a18", label: "Fire" },
  titan: { color: "#f0ab20", color2: "#ffd766", label: "Titan" },
};

function rankMeta(rank) {
  if (!rank) return null;
  const key = rank.toLowerCase();
  return RANK_COLORS[key] || { color: "#9e9e9e", color2: "#9e9e9e", label: rank };
}

// Staff & media groups (mirrors the LuckPerms group ladder, highest first)
const STAFF_META = {
  owner:      { color: "#ff4444", color2: "#c0392b", label: "Owner" },
  manager:    { color: "#ff7733", color2: "#d4551a", label: "Manager" },
  hdev:       { color: "#9b59ff", color2: "#7a3fd4", label: "Head Dev" },
  admin:      { color: "#ff5566", color2: "#d43a4a", label: "Admin" },
  dev:        { color: "#7d8cff", color2: "#5a68d4", label: "Developer" },
  hmod:       { color: "#ffaa22", color2: "#d48a10", label: "Head Mod" },
  srmod:      { color: "#ffc94d", color2: "#d4a52e", label: "Sr. Mod" },
  moderator:  { color: "#52c078", color2: "#3a9c5c", label: "Moderator" },
  builder:    { color: "#f0ab20", color2: "#c78c10", label: "Builder" },
  jrmod:      { color: "#6fd88f", color2: "#4eb06e", label: "Jr. Mod" },
  helper:     { color: "#4a9ee0", color2: "#2f7dc0", label: "Helper" },
  thelper:    { color: "#8fd8e8", color2: "#63b6ca", label: "Trial Helper" },
  streamer:   { color: "#9146ff", color2: "#6b2fd4", label: "Streamer" },
  media:      { color: "#e858c8", color2: "#c238a4", label: "Media" },
  "jr.media": { color: "#f08fd8", color2: "#d465b6", label: "Jr. Media" },
};

// Staff roles that don't get the internal Copilot tool
const COPILOT_EXCLUDED_STAFF = ["streamer", "media", "jr.media"];

function canDownloadCopilot(staff) {
  if (!staff) return false;
  return !COPILOT_EXCLUDED_STAFF.includes(staff.toLowerCase());
}

function staffMeta(staff) {
  if (!staff) return null;
  const key = staff.toLowerCase();
  return STAFF_META[key] || { color: "#c0c0c0", color2: "#909090", label: staff };
}

function formatPlaytime(seconds) {
  if (seconds == null) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function formatLastSeen(datetime) {
  if (!datetime) return "—";
  const then = new Date(datetime.replace(" ", "T"));
  if (isNaN(then)) return datetime;
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 5) return "Just now";
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return then.toLocaleDateString();
}

function isOnline(datetime) {
  if (!datetime) return false;
  const then = new Date(datetime.replace(" ", "T"));
  return !isNaN(then) && Date.now() - then.getTime() < 5 * 60000;
}

function formatVerifiedSince(datetime) {
  if (!datetime) return { date: "—", sub: null };
  const then = new Date(datetime.replace(" ", "T"));
  if (isNaN(then)) return { date: datetime, sub: null };
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  let sub;
  if (days < 1) sub = "today";
  else if (days < 30) sub = `${days} day${days === 1 ? "" : "s"} ago`;
  else if (days < 365) sub = `${Math.floor(days / 30)} month${Math.floor(days / 30) === 1 ? "" : "s"} ago`;
  else sub = `${Math.floor(days / 365)} year${Math.floor(days / 365) === 1 ? "" : "s"} ago`;
  return { date: then.toLocaleDateString(), sub };
}

function formatFirstJoined(datetime, season) {
  if (!datetime) return { date: "—", sub: null };
  const then = new Date(datetime.replace(" ", "T"));
  const date = isNaN(then) ? datetime : then.toLocaleDateString();
  return { date, sub: season != null ? `Season ${season}` : null };
}

// Standard Minecraft chat color codes (§0-§f / &0-&f)
const MC_COLORS = {
  "0": "#000000", "1": "#0000AA", "2": "#00AA00", "3": "#00AAAA",
  "4": "#AA0000", "5": "#AA00AA", "6": "#FFAA00", "7": "#AAAAAA",
  "8": "#555555", "9": "#5555FF", a: "#55FF55", b: "#55FFFF",
  c: "#FF5555", d: "#FF55FF", e: "#FFFF55", f: "#FFFFFF",
};

// Splits Minecraft-formatted text (§/& codes) into styled segments for rendering
function parseMcText(text) {
  if (!text) return [];
  const segments = [];
  let color = null;
  let bold = false, italic = false, underline = false, strikethrough = false;
  let buf = "";

  const flush = () => {
    if (buf) segments.push({ text: buf, color, bold, italic, underline, strikethrough });
    buf = "";
  };

  const chars = [...text];
  for (let i = 0; i < chars.length; i++) {
    const ch = chars[i];
    if ((ch === "§" || ch === "&") && chars[i + 1]) {
      const code = chars[i + 1].toLowerCase();

      // Bungee-style hex: &x&R&R&G&G&B&B (each hex digit prefixed by its own marker+char)
      if (code === "x") {
        let hex = "";
        let j = i + 2;
        for (let k = 0; k < 6; k++) {
          if ((chars[j] === "§" || chars[j] === "&") && /[0-9a-f]/i.test(chars[j + 1] || "")) {
            hex += chars[j + 1];
            j += 2;
          } else {
            hex = null;
            break;
          }
        }
        if (hex) {
          flush();
          color = "#" + hex;
          bold = italic = underline = strikethrough = false;
          i = j - 1;
          continue;
        }
      }

      if (MC_COLORS[code]) {
        flush();
        color = MC_COLORS[code];
        bold = italic = underline = strikethrough = false;
        i++;
        continue;
      }
      if (code === "l") { flush(); bold = true; i++; continue; }
      if (code === "o") { flush(); italic = true; i++; continue; }
      if (code === "n") { flush(); underline = true; i++; continue; }
      if (code === "m") { flush(); strikethrough = true; i++; continue; }
      if (code === "r") { flush(); color = null; bold = italic = underline = strikethrough = false; i++; continue; }
    }
    buf += ch;
  }
  flush();
  return segments;
}

function McText({ text, className }) {
  const segments = parseMcText(text);
  if (!segments.length) return null;
  return (
    <span className={className}>
      {segments.map((s, i) => (
        <span
          key={i}
          style={{
            color: s.color || "inherit",
            fontWeight: s.bold ? 700 : undefined,
            fontStyle: s.italic ? "italic" : undefined,
            textDecoration: [s.underline && "underline", s.strikethrough && "line-through"]
              .filter(Boolean).join(" ") || undefined,
          }}
        >
          {s.text}
        </span>
      ))}
    </span>
  );
}

// "DIAMOND_SWORD" -> "Diamond Sword"
function formatMaterialName(material) {
  if (!material) return "";
  return material
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

const HAS_MC_CODES = /[§&][0-9a-fk-or]/i;

function McLine({ text, className }) {
  return HAS_MC_CODES.test(text) ? (
    <McText text={text} className={className} />
  ) : (
    <span className={className}>{text}</span>
  );
}

function ItemTooltip({ item }) {
  if (!item?.material) return null;
  return (
    <div className="mc-tooltip">
      <McLine text={item.displayName || formatMaterialName(item.material)} className="mc-tooltip-name" />
      {Array.isArray(item.enchants) && item.enchants.length > 0 && (
        <div className="mc-tooltip-enchants">
          {item.enchants.map((line, i) => (
            <McLine key={i} text={line} className="mc-tooltip-enchant-line" />
          ))}
        </div>
      )}
      {Array.isArray(item.lore) && item.lore.length > 0 && (
        <div className="mc-tooltip-lore">
          {item.lore.map((line, i) => (
            <McLine key={i} text={line} className="mc-tooltip-lore-line" />
          ))}
        </div>
      )}
      {item.root && (
        <div className="mc-tooltip-root">
          <span className="mc-tooltip-root-name">{item.root} Root</span>
          {item.root_tier != null && <span className="mc-tooltip-root-tier">T{item.root_tier}</span>}
        </div>
      )}
      {item.amount > 1 && <span className="mc-tooltip-amount">×{item.amount}</span>}
    </div>
  );
}

// Positions the tooltip in fixed viewport coordinates from the hovered slot's rect,
// clamped so it never runs past the screen edge - rendered via portal into <body> so
// it isn't cropped by the account modal's own scroll container.
function FloatingItemTooltip({ item, rect }) {
  if (!rect) return null;
  const margin = 10;
  const estWidth = 240;
  const estHeight = 160;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let align = "center";
  let left = rect.left + rect.width / 2;
  if (left - estWidth / 2 < margin) align = "left";
  else if (left + estWidth / 2 > vw - margin) align = "right";
  if (align === "left") left = rect.left;
  else if (align === "right") left = rect.right;

  let flip = rect.top - estHeight < margin;
  const top = flip ? rect.bottom + margin : rect.top - margin;

  const translateX = align === "left" ? "0" : align === "right" ? "-100%" : "-50%";
  const translateY = flip ? "0" : "-100%";

  return createPortal(
    <div
      className="mc-tooltip-portal"
      style={{ position: "fixed", left, top, transform: `translate(${translateX}, ${translateY})` }}
    >
      <ItemTooltip item={item} />
    </div>,
    document.body
  );
}

const STAT_ICONS = {
  firstJoined: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4.5" width="18" height="16" rx="2" /><path d="M3 9.5h18" /><path d="M8 2.5v4" /><path d="M16 2.5v4" />
    </svg>
  ),
  playtime: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" />
    </svg>
  ),
  lastOnline: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12.5a14 14 0 0 1 20 0" /><path d="M5.5 16a9 9 0 0 1 13 0" /><circle cx="12" cy="19.5" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  ),
  root: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 21V9" /><path d="M12 9c0-4 3-6.5 7-6.5C19 6.5 16 9 12 9Z" /><path d="M12 13c0-3.2-2.4-5.2-5.6-5.2C6.4 11 8.8 13 12 13Z" />
    </svg>
  ),
  verified: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2.5 19.5 6v5.5c0 4.6-3.2 8.4-7.5 9.5-4.3-1.1-7.5-4.9-7.5-9.5V6Z" /><path d="m8.7 12 2.3 2.3 4.3-4.6" />
    </svg>
  ),
  xp: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m12 2.5 2.6 6 6.4.6-4.8 4.3 1.4 6.3L12 16.4l-5.6 3.3 1.4-6.3L3 9.1l6.4-.6Z" />
    </svg>
  ),
  team: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 21V4" /><path d="M5 4h13l-2.5 4L18 12H5" />
    </svg>
  ),
  inventory: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="15" rx="2" /><path d="M3 11h18" /><path d="M10 11v3h4v-3" />
    </svg>
  ),
};

const NAV = [
  { path: "/", label: "Home" },
  { path: "/store", label: "Store" },
  { path: "/giveaways", label: "Giveaways" },
  { path: "/blogs", label: "Blogs" },
  { path: "/our-staff", label: "Staff" },
  { path: "/rules", label: "Rules" },
  { path: "/apply", label: "Apply" },
  { path: "/SeasonInfo", label: "Season Info" },
];

function Navbar() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [showCopilot, setShowCopilot] = useState(false);
  const [code, setCode] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(null);
  const [codeFocused, setCodeFocused] = useState(true);
  const codeInputRef = useRef(null);
  // Inventory item tooltip - portaled to <body> so it can escape the modal's
  // scroll clipping instead of being cropped by account-modal--xl's overflow-y
  const [hoverItem, setHoverItem] = useState(null); // { item, rect } | null

  function handleItemHover(e, item) {
    if (!item?.material) return;
    setHoverItem({ item, rect: e.currentTarget.getBoundingClientRect() });
  }
  function handleItemLeave() {
    setHoverItem(null);
  }

  // Any localStorage["mc_user"] write must go through here so AuthContext (used by other
  // pages like Ranks to check login state) picks up the change immediately - "storage"
  // events don't fire in the tab that made the write, only other tabs.
  function syncUser(next) {
    setUser(next);
    window.dispatchEvent(new Event("mc_user_changed"));
  }

  // Restore session + refresh profile (rank/playtime) on every page load
  useEffect(() => {
    const saved = localStorage.getItem("mc_user");
    if (!saved) return;
    let parsed;
    try { parsed = JSON.parse(saved); } catch { localStorage.removeItem("mc_user"); return; }
    if (!parsed?.token) { localStorage.removeItem("mc_user"); return; }
    syncUser(parsed);

    api("playergetprofile", { token: parsed.token }).then((res) => {
      if (res.isSuccess && res.data) {
        const fresh = profileToUser(parsed.token, res.data);
        localStorage.setItem("mc_user", JSON.stringify(fresh));
        syncUser(fresh);
      } else if (res.message === "Session expired") {
        localStorage.removeItem("mc_user");
        syncUser(null);
      }
    });
  }, []);

  // Lets other pages (e.g. the Ranks store) open this navbar's login modal via AuthContext
  useEffect(() => {
    const open = () => setShowLogin(true);
    window.addEventListener("mc_open_login", open);
    return () => window.removeEventListener("mc_open_login", open);
  }, []);

  function profileToUser(token, p) {
    // inventory may arrive as a JSON string from the plugin
    let inventory = p.inventory ?? null;
    if (typeof inventory === "string") {
      try { inventory = JSON.parse(inventory); } catch { inventory = null; }
    }
    let armor = p.armor ?? null;
    if (typeof armor === "string") {
      try { armor = JSON.parse(armor); } catch { armor = null; }
    }
    return {
      token,
      ign: p.player_name,
      uuid: p.uuid,
      rank: p.rank_name || null,
      staff: p.staff_rank || null,
      root: p.root_name || null,
      rootTier: p.root_tier || null,
      playtime: p.playtime_seconds,
      lastSeen: p.last_seen,
      verifiedAt: p.verified_at,
      xpLevel: p.xp_level ?? null,
      xpProgress: p.xp_progress ?? null, // 0..1 toward next level
      team: p.team_name || null,
      teamColor: p.team_color || null,
      teamRole: p.team_role || null,
      firstJoined: p.first_joined || null,
      season: p.season ?? null,
      inventory, // [{ slot: 0-35, material, amount }]
      armor,     // { helmet, chestplate, leggings, boots, offhand }
      avatar: `https://mc-heads.net/avatar/${p.player_name}/80`,
    };
  }

  // Single theme now — pin default and clear any saved "bright" from before the
  // toggle was removed, so nobody's stuck in the old bright palette.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "default");
    localStorage.removeItem("site_theme");
  }, []);

  async function handleLogin(e) {
    e.preventDefault();
    const clean = code.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    if (clean.length < 4 || loggingIn) return;
    setLoggingIn(true);
    setLoginError("");
    const res = await api("playerverifylogin", { code: clean });
    setLoggingIn(false);
    if (!res.isSuccess || !res.data?.token) {
      setLoginError(res.message || "Invalid or expired code.");
      return;
    }
    const userData = profileToUser(res.data.token, res.data);
    localStorage.setItem("mc_user", JSON.stringify(userData));
    syncUser(userData);
    // Show the success animation, then close the modal
    setLoginSuccess(userData);
    setTimeout(() => {
      setShowLogin(false);
      setLoginSuccess(null);
      setCode("");
    }, 1800);
  }

  function handleLogout() {
    if (user?.token) api("playerlogout", { token: user.token });
    localStorage.removeItem("mc_user");
    syncUser(null);
    setShowAccount(false);
  }

  return (
    <>
      <FadeIn y={-20} duration={0.6} className="navbar">
        <div
          className="navbar-brand"
          onClick={() => navigate("/")}
          style={{ cursor: "pointer" }}
        >
          <img src={logo} alt="SimpleMC" className="navbar-logo-full" />
          <img src={logoS} alt="SimpleMC" className="navbar-logo-s" />
        </div>

        <div className="navbar-links">
          <div className="navbar-links-pill">
            {NAV.map(({ path, label }) => (
              <NavLink
                key={path}
                to={path}
                end={path === "/"}
                className={({ isActive }) =>
                  `nav-link${isActive ? " active" : ""}${path === "/store" ? " nav-link--store" : ""}`
                }
              >
                {label}
              </NavLink>
            ))}
          </div>
          {isLocalDev() && (
            <NavLink to="/wiki/items" className="nav-wiki-link">
              Wiki
            </NavLink>
          )}
        </div>

        <div className="nav-actions desktop-only">
          {user ? (
            <div className="nav-user" onClick={() => setShowAccount(true)} title="View account">
              <img src={user.avatar} alt="Avatar" className="nav-user-head" />
              <span className="nav-user-name">{user.ign}</span>
              {user.staff && staffMeta(user.staff) ? (
                <span
                  className="nav-user-rank"
                  style={{ "--rank-c": staffMeta(user.staff).color, "--rank-c2": staffMeta(user.staff).color2 }}
                >
                  {staffMeta(user.staff).label}
                </span>
              ) : user.rank && rankMeta(user.rank) ? (
                <span
                  className="nav-user-rank"
                  style={{ "--rank-c": rankMeta(user.rank).color, "--rank-c2": rankMeta(user.rank).color2 }}
                >
                  {rankMeta(user.rank).label}
                </span>
              ) : null}
            </div>
          ) : (
            <button className="nav-login" onClick={() => setShowLogin(true)}>
              Login
            </button>
          )}
          <button
            onClick={() => window.open("https://discord.com/invite/simplemcnetwork", "_blank")}
            className="nav-discord nav-discord-desktop"
          >
            Discord
          </button>
        </div>

        <button
          className={`nav-hamburger${menuOpen ? " open" : ""}`}
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Toggle Menu"
        >
          <span />
          <span />
          <span />
        </button>
      </FadeIn>

      {/* Login Modal — verify code from in-game */}
      <AnimatePresence>
      {showLogin && (
        <motion.div
          className="login-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={() => !loginSuccess && setShowLogin(false)}
        >
          <motion.div
            className={`login-modal${loginSuccess ? " is-success" : ""}`}
            initial={{ opacity: 0, y: 32, scale: 0.93 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 380, damping: 30 }}
            onClick={e => e.stopPropagation()}
          >
            {loginSuccess ? (
              <div className="login-success">
                <div className="login-success-ring">
                  <img src={loginSuccess.avatar} alt={loginSuccess.ign} />
                  <svg className="login-success-check" viewBox="0 0 52 52" aria-hidden="true">
                    <circle className="login-success-check-circle" cx="26" cy="26" r="24" fill="none" />
                    <path className="login-success-check-mark" fill="none" d="M15 27l8 8 15-16" />
                  </svg>
                </div>
                <h2>Welcome, {loginSuccess.ign}!</h2>
                <p>You're verified and logged in.</p>
              </div>
            ) : (
              <>
                <div className="login-verify-badge">
                  <img src={commandBlock} alt="" />
                </div>
                <h2>Link your account</h2>
                <p>Log in with your Minecraft account — no password needed.</p>
                <ol className="login-steps">
                  <li><span>1</span>Join the server</li>
                  <li><span>2</span>Run <code>/verify</code> in chat</li>
                  <li><span>3</span>Enter the code below</li>
                </ol>
                <form onSubmit={handleLogin}>
                  <div
                    className={`login-code-wrap${loginError ? " has-error" : ""}`}
                    onClick={() => codeInputRef.current?.focus()}
                  >
                    <input
                      ref={codeInputRef}
                      className="login-code-hidden"
                      type="text"
                      value={code}
                      maxLength={8}
                      autoFocus
                      autoComplete="off"
                      spellCheck={false}
                      aria-label="Verification code"
                      onFocus={() => setCodeFocused(true)}
                      onBlur={() => setCodeFocused(false)}
                      onChange={e => {
                        setCode(e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase());
                        setLoginError("");
                      }}
                    />
                    <div className="login-code-cells" aria-hidden="true">
                      {Array.from({ length: Math.max(6, code.length) }).map((_, i) => (
                        <span
                          key={i}
                          className={`login-code-cell${i < code.length ? " filled" : ""}${
                            codeFocused && i === Math.min(code.length, 7) ? " active" : ""
                          }`}
                        >
                          {code[i] || ""}
                        </span>
                      ))}
                    </div>
                  </div>
                  {loginError && <div className="login-error">{loginError}</div>}
                  <button type="submit" className="btn-primary login-submit" disabled={loggingIn}>
                    {loggingIn ? (
                      <span className="login-spinner-wrap"><span className="login-spinner" />Verifying…</span>
                    ) : (
                      "Verify & Login"
                    )}
                  </button>
                </form>
                <button className="login-cancel" onClick={() => setShowLogin(false)}>Maybe Later</button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      {/* Account Info Modal */}
      <AnimatePresence>
      {showAccount && user && (() => {
        const staff = staffMeta(user.staff);
        const rank = rankMeta(user.rank);
        // Staff color takes over the banner; falls back to rank, then site accent
        const theme = staff || rank || { color: "var(--accent)", color2: "var(--accent-dim)" };
        const online = isOnline(user.lastSeen);
        const verified = formatVerifiedSince(user.verifiedAt);
        const firstJoined = formatFirstJoined(user.firstJoined, user.season);
        // Inventory: slots 0-8 hotbar, 9-35 main storage
        const invMap = {};
        if (Array.isArray(user.inventory)) {
          user.inventory.forEach((it) => {
            if (it && it.slot != null) invMap[it.slot] = it;
          });
        }
        const hasInv = Array.isArray(user.inventory);
        const armor = user.armor || {};
        const armorSlots = [
          { key: "helmet", ph: "H" },
          { key: "chestplate", ph: "C" },
          { key: "leggings", ph: "L" },
          { key: "boots", ph: "B" },
          { key: "offhand", ph: "O" },
        ];
        const xpPct = user.xpProgress != null
          ? Math.max(0, Math.min(1, Number(user.xpProgress))) * 100
          : null;
        const stats = [
          { key: "firstJoined", icon: STAT_ICONS.firstJoined, label: "First Joined", value: firstJoined.date, sub: firstJoined.sub },
          { key: "playtime", icon: STAT_ICONS.playtime, label: "Playtime", value: formatPlaytime(user.playtime) },
          {
            key: "lastOnline", icon: STAT_ICONS.lastOnline, label: "Last online",
            value: online ? <span className="account-stat-online">Online now</span> : formatLastSeen(user.lastSeen),
          },
          {
            key: "root", icon: STAT_ICONS.root, label: "Current Root",
            value: user.root ? (
              <>
                {user.root}
                {user.rootTier ? <span className="account-root-tier">T{user.rootTier}</span> : null}
              </>
            ) : "None",
          },
          { key: "verified", icon: STAT_ICONS.verified, label: "Verified", value: verified.date, sub: verified.sub },
        ];
        return (
          <motion.div
            className="login-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setShowAccount(false)}
          >
            <motion.div
              className="login-modal account-modal account-modal--xl"
              initial={{ opacity: 0, y: 32, scale: 0.93 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.96 }}
              transition={{ type: "spring", stiffness: 380, damping: 30 }}
              onClick={e => e.stopPropagation()}
              style={{ "--rank-c": theme.color, "--rank-c2": theme.color2 }}
            >
              <button
                className="account-close"
                onClick={() => setShowAccount(false)}
                aria-label="Close"
              >
                ✕
              </button>
              <div className="account-grid">
                <motion.aside
                  className="account-side"
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.35, delay: 0.06 }}
                >
                  <div className="account-head">
                    <img src={`https://mc-heads.net/avatar/${user.ign}/100`} alt={user.ign} />
                    {online && <span className="account-online-badge" title="Online now" />}
                  </div>
                  <h2>{user.ign}</h2>
                  <div className="account-chips">
                    {staff && (
                      <span
                        className="account-rank-chip account-staff-chip"
                        style={{ "--chip-c": staff.color }}
                      >
                        ★ {staff.label}
                      </span>
                    )}
                    {rank ? (
                      <span className="account-rank-chip" style={{ "--chip-c": rank.color }}>
                        {rank.label} Rank
                      </span>
                    ) : !staff ? (
                      <span className="account-rank-chip account-rank-none">No rank yet</span>
                    ) : null}
                  </div>

                  <div className="account-srows">
                    {stats.map((s, i) => (
                      <motion.div
                        key={s.key}
                        className="account-srow"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.14 + i * 0.05 }}
                      >
                        <span className="account-stat-icon">{s.icon}</span>
                        <span className="account-srow-label">{s.label}</span>
                        <span className="account-srow-value">
                          {s.value}
                          {s.sub && <span className="account-stat-sub"> · {s.sub}</span>}
                        </span>
                      </motion.div>
                    ))}
                  </div>

                  <div className="account-actions">
                    {!user.rank && (
                      <button
                        className="btn-primary login-submit account-upsell"
                        onClick={() => { setShowAccount(false); navigate("/store"); }}
                      >
                        Browse Store
                      </button>
                    )}
                    {canDownloadCopilot(user.staff) && (
                      <button
                        className="account-copilot-btn"
                        onClick={() => setShowCopilot(true)}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 19h14" />
                        </svg>
                        Download Copilot
                      </button>
                    )}
                    <button className="account-logout" onClick={handleLogout}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" />
                      </svg>
                      Log out
                    </button>
                  </div>
                </motion.aside>

                <motion.div
                  className="account-main"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.14 }}
                >
                  <div className="account-panels">
                    <div className="account-panel">
                      <div className="account-panel-label">
                        <span className="account-stat-icon">{STAT_ICONS.xp}</span>
                        XP Level
                      </div>
                      {user.xpLevel != null ? (
                        <div className="account-xp-row">
                          <span className="account-xp-orb">{user.xpLevel}</span>
                          <div className="account-xp-bar">
                            <div
                              className="account-xp-fill"
                              style={{ width: `${xpPct ?? 0}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="account-panel-empty">Sync coming soon</span>
                      )}
                    </div>
                    <div className="account-panel">
                      <div className="account-panel-label">
                        <span className="account-stat-icon">{STAT_ICONS.team}</span>
                        Team
                      </div>
                      {user.team ? (
                        <div className="account-team-display">
                          <span className="account-team-name">
                            <McText text={user.team} />
                          </span>
                          {user.teamRole && (
                            <span className="account-team-role">{user.teamRole}</span>
                          )}
                        </div>
                      ) : (
                        <span className="account-panel-empty">No team</span>
                      )}
                    </div>
                  </div>

                  <div className="account-inv">
                    <div className="account-panel-label account-inv-label">
                      <span className="account-stat-icon">{STAT_ICONS.inventory}</span>
                      Inventory
                      {!hasInv && <span className="account-inv-soon">Live sync coming soon</span>}
                    </div>
                    <div className="account-inv-armor">
                      {armorSlots.map(({ key, ph }) => (
                        <div
                          key={key}
                          className="account-inv-slot"
                          onMouseEnter={(e) => handleItemHover(e, armor[key])}
                          onMouseLeave={handleItemLeave}
                        >
                          {armor[key]?.material ? (
                            <McIcon materialKey={armor[key].material} size={34} />
                          ) : (
                            <span className="account-inv-ph">{ph}</span>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className={`account-inv-grid${hasInv ? "" : " is-empty"}`}>
                      {Array.from({ length: 27 }, (_, i) => i + 9).map((slot) => (
                        <div
                          key={slot}
                          className="account-inv-slot"
                          onMouseEnter={(e) => handleItemHover(e, invMap[slot])}
                          onMouseLeave={handleItemLeave}
                        >
                          {invMap[slot]?.material && <McIcon materialKey={invMap[slot].material} size={34} />}
                          {invMap[slot]?.amount > 1 && (
                            <span className="account-inv-count">{invMap[slot].amount}</span>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className={`account-inv-grid account-inv-hotbar${hasInv ? "" : " is-empty"}`}>
                      {Array.from({ length: 9 }, (_, slot) => (
                        <div
                          key={slot}
                          className="account-inv-slot"
                          onMouseEnter={(e) => handleItemHover(e, invMap[slot])}
                          onMouseLeave={handleItemLeave}
                        >
                          {invMap[slot]?.material && <McIcon materialKey={invMap[slot].material} size={34} />}
                          {invMap[slot]?.amount > 1 && (
                            <span className="account-inv-count">{invMap[slot].amount}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        );
      })()}
      </AnimatePresence>

      {showAccount && hoverItem && (
        <FloatingItemTooltip item={hoverItem.item} rect={hoverItem.rect} />
      )}

      {/* Copilot Download Modal — staff only, gated server-side too */}
      <AnimatePresence>
      {showCopilot && user && (
        <motion.div
          className="login-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={() => setShowCopilot(false)}
        >
          <motion.div
            className="login-modal copilot-modal"
            initial={{ opacity: 0, y: 32, scale: 0.93 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 380, damping: 30 }}
            onClick={e => e.stopPropagation()}
          >
            <button className="account-close" onClick={() => setShowCopilot(false)} aria-label="Close">✕</button>
            <div className="copilot-badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="4" y="4" width="16" height="16" rx="4" /><circle cx="9" cy="10" r="1.2" fill="currentColor" stroke="none" />
                <circle cx="15" cy="10" r="1.2" fill="currentColor" stroke="none" /><path d="M8.5 14.5c1 1 6 1 7 0" />
              </svg>
            </div>
            <h2>SimpleMC Copilot</h2>
            <p>The internal staff AI assistant desktop app. Sign in with the account you create on first launch — this download link is tied to your staff session.</p>
            <form onSubmit={e => e.preventDefault()} style={{ gap: "0.7rem" }}>
              <a
                className="btn-primary login-submit"
                style={{ textDecoration: "none", display: "block", textAlign: "center" }}
                href={`${SITE_BASE}/download.php?token=${encodeURIComponent(user.token)}`}
                onClick={() => setTimeout(() => setShowCopilot(false), 400)}
              >
                Download Installer
              </a>
              <a
                className="login-cancel"
                style={{ display: "block", textAlign: "center" }}
                href={`${SITE_BASE}/download.php?token=${encodeURIComponent(user.token)}&exe=1`}
                onClick={() => setTimeout(() => setShowCopilot(false), 400)}
              >
                Portable .exe instead
              </a>
            </form>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      <div className={`nav-mobile-drawer${menuOpen ? " open" : ""}`}>
          {user && (
            <div
              className="nav-mobile-user"
              onClick={() => { setShowAccount(true); setMenuOpen(false); }}
            >
              <img src={user.avatar} alt="Avatar" />
              <span>{user.ign}</span>
              <button
                onClick={(e) => { e.stopPropagation(); handleLogout(); }}
                className="btn-ghost"
                style={{ fontSize: '10px' }}
              >
                Logout
              </button>
            </div>
          )}
          <div className="nav-mobile-links">
            {NAV.map(({ path, label }) => (
              <NavLink
                key={path}
                to={path}
                end={path === "/"}
                className={({ isActive }) =>
                  `nav-mobile-link${isActive ? " active" : ""}${path === "/store" ? " nav-mobile-link--store" : ""}`
                }
                onClick={() => setMenuOpen(false)}
              >
                {label}
              </NavLink>
            ))}
            {isLocalDev() && (
              <NavLink to="/wiki" className="nav-mobile-link" onClick={() => setMenuOpen(false)}>
                Wiki ↗
              </NavLink>
            )}
            {!user && (
              <button className="nav-mobile-link" onClick={() => { setShowLogin(true); setMenuOpen(false); }}>
                Login
              </button>
            )}
          </div>

          <div className="nav-mobile-footer" style={{ marginTop: 'auto', padding: '1.5rem 0 0.5rem' }}>
            <button
              onClick={() => { window.open("https://discord.com/invite/simplemcnetwork", "_blank"); setMenuOpen(false); }}
              className="nav-discord nav-discord-drawer"
            >
              Join Discord
            </button>
          </div>
        </div>
    </>
  );
}

function SiteFooter() {
  const [copied, setCopied] = useState(false);
  const ip = "simplemcnetwork.net";

  function copyIp() {
    navigator.clipboard.writeText(ip).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <footer className="site-footer">
      <div className="footer-grid">
        <div className="footer-col footer-col-brand">
          <img src={logo} alt="SimpleMC" className="footer-logo" />
          <p className="footer-tagline">
            A survival SMP built around community, seasons and custom roots.
          </p>
          <button className="footer-ip" onClick={copyIp} title="Copy server IP">
            {ip}
            <span className="footer-ip-copy">{copied ? "Copied!" : "Copy"}</span>
          </button>
        </div>

        <div className="footer-col">
          <h4>Explore</h4>
          <NavLink to="/">Home</NavLink>
          <NavLink to="/store">Store</NavLink>
          {isLocalDev() && <NavLink to="/wiki/items">Wiki</NavLink>}
        </div>

        <div className="footer-col">
          <h4>Community</h4>
          <NavLink to="/blogs">Blogs</NavLink>
          <NavLink to="/our-staff">Staff</NavLink>
          <a
            href="https://discord.com/invite/simplemcnetwork"
            target="_blank"
            rel="noreferrer"
          >
            Discord ↗
          </a>
        </div>
      </div>

      <div className="footer-bottom">
        <span>© {new Date().getFullYear()} SimpleMC Network · Season 1</span>
        <span className="footer-legal">
          Not affiliated with Mojang Studios or Microsoft.
        </span>
      </div>
    </footer>
  );
}

function Layout() {
  const location = useLocation();
  useEffect(() => {
    const last = localStorage.getItem("lastVisit");
    const now = Date.now();
    if (!last || now - last > 300000) {
      localStorage.setItem("lastVisit", now);
      api("recordvisit");
    }
  }, []);

  return (
    <AuthProvider>
      <div>
        <Navbar />
        <DiscountBanner />
        <main>
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<PageWrapper><Home /></PageWrapper>} />
              <Route path="/SeasonInfo" element={<PageWrapper><SeasonInfo /></PageWrapper>} />
              <Route path="/store" element={<PageWrapper><Ranks /></PageWrapper>} />
              <Route path="/ranks" element={<Navigate to="/store" replace />} />
              <Route path="/apply" element={<PageWrapper><Apply /></PageWrapper>} />
              <Route path="/rules" element={<PageWrapper><Rules /></PageWrapper>} />
              <Route path="/blogs" element={<PageWrapper><Blogs /></PageWrapper>} />
              <Route path="/blogs/:id" element={<PageWrapper><BlogPost /></PageWrapper>} />
              <Route path="/our-staff" element={<PageWrapper><StaffPage /></PageWrapper>} />
              <Route path="/giveaways" element={<PageWrapper><GiveawayPage /></PageWrapper>} />
              <Route path="*" element={<PageWrapper><ComingSoon title="Page Not Found" /></PageWrapper>} />
            </Routes>
          </AnimatePresence>
        </main>
        <SiteFooter />
      </div>
    </AuthProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin" element={<Navigate to="/wiki/admin" replace />} />
        {AdminPage && (
          <Route
            path="/wiki/admin"
            element={<Suspense fallback={null}><AdminPage /></Suspense>}
          />
        )}
        <Route path="/wiki/*" element={isLocalDev() ? <Wiki /> : <Navigate to="/" replace />} />
        <Route path="/*" element={<Layout />} />
      </Routes>
    </BrowserRouter>
  );
}
